import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../api/client.js';
import clsx from 'clsx';

const TABS = [
  { id: 'revenue', label: 'Revenue' },
  { id: 'lapsed',  label: 'Lapsed customers' },
  { id: 'crosssell', label: 'Cross-sell candidates' },
  { id: 'pipeline', label: 'Pipeline' },
];

function fmt$(v) {
  const n = Number(v || 0);
  if (n === 0) return '$0';
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + Math.round(n).toLocaleString();
  return '$' + n.toFixed(0);
}
function fmtMonth(year, month) {
  return `${year}-${String(month).padStart(2,'0')}`;
}

export default function Parts() {
  const [tab, setTab] = useState('revenue');

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Parts</div>
        <h1 className="text-2xl">Parts</h1>
        <div className="rule-crimson" />
      </header>

      <div className="border-b border-panelDark flex gap-1">
        {TABS.map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-2 text-sm border-b-2 -mb-px',
              tab === t.id ? 'border-crimson text-crimson font-medium' : 'border-transparent text-navy hover:text-crimson'
            )}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'revenue'  && <RevenueTab  />}
      {tab === 'lapsed'   && <LapsedTab   />}
      {tab === 'crosssell' && <CrossSellTab direction="parts_strong_service_weak"
        explainer="Customers buying parts from us but not bringing trailers in for service. Pitch service." />}
      {tab === 'pipeline' && <PipelineTab />}
    </div>
  );
}

function RevenueTab() {
  const q = useQuery({
    queryKey: ['parts', 'revenue'],
    queryFn: () => api.get('/parts/revenue').then(r => r.data),
  });

  if (q.isLoading) return <div className="text-muted">Loading…</div>;
  if (q.isError)   return <div className="text-crimson">Failed to load.</div>;

  const trend = q.data?.trend || [];
  const top   = q.data?.top_customers || [];
  const branches = q.data?.by_branch || [];

  // Trend: last 24 months. Compute current 12mo and prior 12mo.
  const last12 = trend.slice(-12).reduce((s, r) => s + Number(r.parts_sales || 0), 0);
  const prior12 = trend.slice(-24, -12).reduce((s, r) => s + Number(r.parts_sales || 0), 0);
  const yoy = prior12 > 0 ? (last12 - prior12) / prior12 : null;
  const maxMonth = trend.reduce((m, r) => Math.max(m, Number(r.parts_sales || 0)), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Stat label="Last 12 months" value={fmt$(last12)} />
        <Stat label="Prior 12 months" value={fmt$(prior12)} />
        <Stat label="YoY"
              value={yoy == null ? '—' : `${yoy >= 0 ? '+' : ''}${(yoy * 100).toFixed(1)}%`}
              highlight={yoy != null && yoy < 0} />
      </div>

      <section>
        <h2 className="text-sm font-semibold text-navy uppercase tracking-wide mb-2">Monthly trend (last 24 months)</h2>
        <div className="bg-white border border-panelDark rounded p-3 space-y-1">
          {trend.map(r => (
            <div key={`${r.year}-${r.month}`} className="flex items-center gap-2 text-xs">
              <span className="font-mono text-muted w-16">{fmtMonth(r.year, r.month)}</span>
              <div className="flex-1 bg-panel rounded h-4 relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-navy/80 rounded"
                     style={{ width: `${maxMonth ? (Number(r.parts_sales) / maxMonth) * 100 : 0}%` }} />
              </div>
              <span className="w-24 text-right tabular-nums">{fmt$(r.parts_sales)}</span>
              <span className="w-12 text-right text-muted text-[11px]">{r.parts_invoices} inv</span>
            </div>
          ))}
          {trend.length === 0 && <div className="text-muted text-sm">No data.</div>}
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-4">
        <section>
          <h2 className="text-sm font-semibold text-navy uppercase tracking-wide mb-2">By branch (12mo)</h2>
          <div className="bg-white border border-panelDark rounded">
            <table className="w-full text-sm">
              <tbody>
                {branches.map(b => (
                  <tr key={b.branch_id} className="border-t border-panel first:border-t-0">
                    <td className="px-3 py-1.5">{b.branch_name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(b.parts_12mo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold text-navy uppercase tracking-wide mb-2">Top customers (12mo)</h2>
          <div className="bg-white border border-panelDark rounded">
            <table className="w-full text-sm">
              <tbody>
                {top.slice(0, 15).map(c => (
                  <tr key={c.customer_id} className="border-t border-panel first:border-t-0">
                    <td className="px-3 py-1.5 truncate">
                      <Link to={`/customers/${c.customer_id}`} className="text-navy hover:text-crimson">{c.name}</Link>
                      {Number(c.row_count) > 1 && (
                        <span className="ml-2 text-[10px] text-muted">({c.row_count} rows merged)</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(c.parts_12mo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

function LapsedTab() {
  const [silence, setSilence]   = useState(3);
  const [baseline, setBaseline] = useState(12);
  const [minAvg, setMinAvg]     = useState(100);
  const [branchId, setBranchId] = useState('');

  const q = useQuery({
    queryKey: ['parts', 'lapsed', silence, baseline, minAvg, branchId],
    queryFn: () => {
      const p = new URLSearchParams();
      p.set('silence_threshold_months', silence);
      p.set('baseline_months', baseline);
      p.set('min_baseline_avg', minAvg);
      p.set('limit', '100');
      if (branchId) p.set('branch_id', branchId);
      return api.get(`/parts/lapsed-customers?${p.toString()}`).then(r => r.data);
    },
    keepPreviousData: true,
  });

  const totalAtRisk = (q.data?.data || []).reduce((s, r) => s + Number(r.at_risk_score || 0), 0);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Customers whose past parts buying has gone quiet. Score = baseline monthly average × months silent.
        Higher = bigger at-risk dollars.
      </p>

      <div className="bg-panel rounded p-2 flex flex-wrap items-center gap-3 text-sm">
        <Field label="Silence threshold" suffix="mo">
          <input type="number" min="1" max="24" value={silence}
            onChange={e => setSilence(parseInt(e.target.value, 10) || 3)}
            className="w-16 px-2 py-1 rounded border border-panelDark bg-white" />
        </Field>
        <Field label="Baseline window" suffix="mo">
          <input type="number" min="3" max="36" value={baseline}
            onChange={e => setBaseline(parseInt(e.target.value, 10) || 12)}
            className="w-16 px-2 py-1 rounded border border-panelDark bg-white" />
        </Field>
        <Field label="Min baseline" suffix="$/mo">
          <input type="number" min="0" max="100000" step="50" value={minAvg}
            onChange={e => setMinAvg(parseFloat(e.target.value) || 0)}
            className="w-20 px-2 py-1 rounded border border-panelDark bg-white" />
        </Field>
        <select value={branchId} onChange={e => setBranchId(e.target.value)}
                className="px-2 py-1 rounded border border-panelDark bg-white">
          <option value="">All branches</option>
          <option value="3">Fontana</option>
          <option value="2">Fresno</option>
          <option value="1">Lathrop</option>
          <option value="4">Delhi</option>
          <option value="5">Troutdale</option>
        </select>
        <div className="ml-auto text-xs text-muted">
          {q.isLoading ? 'Loading…' : `${(q.data?.data || []).length} rows · ${fmt$(totalAtRisk)} at risk`}
        </div>
      </div>

      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Branch</th>
              <th className="text-right px-3 py-2">Baseline avg</th>
              <th className="text-right px-3 py-2">Silent</th>
              <th className="text-right px-3 py-2">Last buy</th>
              <th className="text-right px-3 py-2">At-risk score</th>
            </tr>
          </thead>
          <tbody>
            {(q.data?.data || []).map(r => (
              <tr key={r.customer_id} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5">
                  <Link to={`/customers/${r.customer_id}`} className="text-navy hover:text-crimson">{r.name}</Link>
                </td>
                <td className="px-3 py-1.5 text-xs text-muted">{r.branch_name || '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(r.baseline_avg)}/mo</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.months_silent}m</td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">{r.last_purchase_at?.slice(0,7) || '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmt$(r.at_risk_score)}</td>
              </tr>
            ))}
            {!q.isLoading && (q.data?.data || []).length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-6">No lapsed customers match these thresholds.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PipelineTab() {
  const q = useQuery({
    queryKey: ['parts', 'pipeline'],
    queryFn: () => api.get('/parts/pipeline').then(r => r.data),
  });
  const rows = q.data?.data || [];

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted">
        Open opportunities tagged Parts. To populate this list, set an opportunity's <em>domain</em>
        to <code>parts</code> when creating or editing it.
      </p>
      <div className="bg-white border border-panelDark rounded">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Stage</th>
              <th className="text-right px-3 py-2">Value</th>
              <th className="text-right px-3 py-2">Activity</th>
              <th className="text-right px-3 py-2">Close by</th>
              <th className="text-left px-3 py-2">Owner</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(o => (
              <tr key={o.id} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5">
                  <Link to={`/pipeline/${o.id}`} className="text-navy hover:text-crimson font-medium">{o.name}</Link>
                </td>
                <td className="px-3 py-1.5">
                  {o.customer_id
                    ? <Link to={`/customers/${o.customer_id}`} className="text-navy hover:text-crimson">{o.customer_name}</Link>
                    : '—'}
                </td>
                <td className="px-3 py-1.5 capitalize">{o.stage}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(o.value_usd)}</td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">
                  {o.source_note_date ? new Date(o.source_note_date).toLocaleDateString() : '—'}
                </td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">{o.expected_close_date || '—'}</td>
                <td className="px-3 py-1.5 text-xs text-muted">{o.owner_name || '—'}</td>
              </tr>
            ))}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted py-6">No parts opportunities yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CrossSellTab({ direction, explainer }) {
  const qc = useQueryClient();
  const [requireUnits, setRequireUnits] = useState(true);
  const q = useQuery({
    queryKey: ['cross-sell', direction, requireUnits],
    queryFn: () => api.get(`/cross-sell?direction=${direction}&limit=100&require_units=${requireUnits}`).then(r => r.data),
  });
  const hide = useMutation({
    mutationFn: (customerId) => api.patch(`/cross-sell/${customerId}/hide`, { hidden: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cross-sell'] }),
  });
  const markTraveling = useMutation({
    mutationFn: (customerId) => api.patch(`/cross-sell/${customerId}/traveling-mechanic`, { traveling: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cross-sell'] }),
  });
  const rows = q.data?.data || [];
  const strongLabel = direction.startsWith('parts_') ? 'Parts (12mo)' : 'Service (12mo)';
  const weakLabel   = direction.startsWith('parts_') ? 'Service (12mo)' : 'Parts (12mo)';
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted">{explainer}</p>
      <div className="flex items-center gap-4">
        <div className="text-xs text-muted">{q.isLoading ? 'Loading…' : `${rows.length} candidates`}</div>
        <label className="flex items-center gap-1.5 text-xs text-navy cursor-pointer">
          <input type="checkbox" checked={requireUnits} onChange={e => setRequireUnits(e.target.checked)}
                 className="rounded" />
          Only customers with vehicles in fleet
        </label>
      </div>
      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Branch</th>
              <th className="text-right px-3 py-2">{strongLabel}</th>
              <th className="text-right px-3 py-2">{weakLabel}</th>
              <th className="text-right px-3 py-2">Last weak buy</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.customer_id} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5">
                  <Link to={`/customers/${r.customer_id}`} className="text-navy hover:text-crimson">{r.name}</Link>
                </td>
                <td className="px-3 py-1.5 text-xs text-muted">{r.branch_name || '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(r.strong_12mo)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(r.weak_12mo)}</td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">{r.last_weak_period?.slice(0,7) || '—'}</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap space-x-3">
                  <button
                    onClick={() => markTraveling.mutate(r.customer_id)}
                    disabled={markTraveling.isPending}
                    title="This customer brings in other people's trailers — exclude always"
                    className="text-xs text-muted hover:underline disabled:opacity-50"
                  >
                    Traveling mechanic
                  </button>
                  <button
                    onClick={() => hide.mutate(r.customer_id)}
                    disabled={hide.isPending}
                    title="Hide this customer from cross-sell lists"
                    className="text-xs text-crimson hover:underline disabled:opacity-50"
                  >
                    Hide
                  </button>
                </td>
              </tr>
            ))}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-6">No cross-sell candidates match these thresholds.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={clsx(
      'border rounded px-3 py-2',
      highlight ? 'bg-rose-50 border-rose-200' : 'bg-panel border-panelDark'
    )}>
      <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Field({ label, suffix, children }) {
  return (
    <label className="flex items-center gap-1 text-xs text-navy">
      {label} {children}
      {suffix && <span className="text-muted">{suffix}</span>}
    </label>
  );
}
